import { FormikProps } from "formik"
import { isEmpty, isNull, isUndefined } from "lodash"
import { RefObject, useEffect, useState } from "react"
import { QueryKey, UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from "react-query"
import { useAlert } from "../../alert/useAlert"
import { useApiResponseErrorValidator } from "../../api/ApiMiddleWare"
import { convertObject } from "./dataHelper"
import setErrorFields from "./formikHelper"

//##############################################
//## STALETIME
//##############################################
export enum StaleTimeEnum {
    DEFAULT = 0,
    LOW = 1000 * 30,
    STATIC = 1000 * 60 * 60,
    HEAVY = 1000 * 60,
    FORM = 1000 * 60 * 5,
    Infinite = Infinity
}

//##############################################
//## MUTATION
//##############################################

export const useMutationCreate = <Type, ReturnType = void>(
    formikRef: RefObject<FormikProps<Type>>,
    apiToCall: (type: Type) => Promise<ReturnType>,
    keysToInvalidate: Array<QueryKey>,
    typeName: string
): UseMutationResult<ReturnType, any, Type, unknown> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const queryClient = useQueryClient();

    const mutateUpdate = useMutation(apiToCall, {

        onSuccess: () => {
            alert.success(`${typeName} Creation: Successful`)
            keysToInvalidate.forEach(async (k) => await queryClient.invalidateQueries(k, { refetchActive: true, refetchInactive: true }))
        },

        onError: (error: any) => {

            validator.validate(error.response)
            alert.error(`${typeName} Creation: Failure`, "")
            setErrorFields(formikRef, error.response.data?.Errors)
        },


    })

    return mutateUpdate
}

export const useMutationUpdate = <Type, ReturnType = void>(
    formikRef: RefObject<FormikProps<Type>>,
    apiToCall: (type: Type) => Promise<ReturnType>,
    keysToInvalidate: Array<QueryKey>,
    typeName: string
): UseMutationResult<ReturnType, any, Type, unknown> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const queryClient = useQueryClient();

    const mutateUpdate = useMutation(apiToCall, {

        onSuccess: async () => {
            alert.success(`${typeName} Update: Successful`)
            keysToInvalidate.forEach(async (k) => await queryClient.invalidateQueries(k, { refetchActive: true, refetchInactive: true }))
        },

        onError: (error: any) => {
            if (error.response?.status === 409) {
                formikRef.current?.setStatus("Conflict")
                alert.error(`${typeName} Update: Conflict`, error.response?.data?.Message)
            }
            else {
                validator.validate(error.response)
                alert.error(`${typeName} Update: Failure`, error.response?.data?.Message)
                setErrorFields(formikRef, error.response?.data?.Errors)
            }
        },

    })

    return mutateUpdate
}

export const useMutationDelete = <Type = number | undefined>(
    apiToCall: (type: Type) => Promise<void>,
    keysToInvalidate: Array<QueryKey>,
    typeName: string,
    formikRef?: RefObject<FormikProps<any>>
): UseMutationResult<void, any, Type, unknown> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const queryClient = useQueryClient();

    const mutateDelete = useMutation(apiToCall, {

        onSuccess: () => {
            alert.success(`${typeName} Deletion: Successful`)
            keysToInvalidate.forEach(async (k) => await queryClient.invalidateQueries(k, { refetchActive: true, refetchInactive: true }))
        },

        onError: (error: any) => {
            validator.validate(error.response)
            alert.error(`${typeName} Deletion: Failure`, error.response?.data?.Message);
            if (formikRef !== undefined)
                setErrorFields(formikRef, error.response?.data?.Errors)
        },

    })

    return mutateDelete
}

export const useMutationCommand = <Type = void, ReturnType = void>(
    apiToCall: (param: Type) => Promise<ReturnType>,
    commandName: string,
    keysToInvalidate?: Array<QueryKey>,
    formikRef?: RefObject<FormikProps<Type>>
): UseMutationResult<ReturnType, any, Type, unknown> => {

    // const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const queryClient = useQueryClient();

    const mutateCommand = useMutation(apiToCall, {

        onSuccess: () => {
            // alert.success(`${commandName} executed`)
            keysToInvalidate?.forEach(async (k) => await queryClient.invalidateQueries(k, { refetchActive: true, refetchInactive: true }))
        },

        onError: (error: any) => {
            validator.validate(error.response)
            // alert.error(`${commandName} execution - Failure`, error.response?.data?.Message);
            if (formikRef)
                setErrorFields(formikRef, error.response.data?.Errors)
        },

    })

    return mutateCommand
}

//##############################################
//## QUERY DATA ARRAY
//##############################################

export const extractQueryDataArray = <Type>(query: UseQueryResult<Array<Type>>): Array<Type> => {
    const data = isEmpty(query.data) || isUndefined(query.data) ? [] : query.data
    return data
}

export interface UseQueryArrayResult<TData> extends Omit<UseQueryResult<Array<TData>, unknown>, "data"> {
    data: Array<TData>
}

export const useQueryArray = <Type extends object>(
    key: QueryKey,
    apiToCall: () => Promise<Array<Type>>,
    typeName: string,
    staleTime: StaleTimeEnum = StaleTimeEnum.DEFAULT
): UseQueryArrayResult<Type> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const query = useQuery<Array<Type>>(key, apiToCall, {

        onError: (error: any) => {
            validator.validate(error.response)
            alert.error(`Error while fetching ${typeName}`, error.response?.data?.Message)
        },

        select: (data: Array<Type>) => data.map((rec: Type) => convertObject<Type>(rec)),

        staleTime: staleTime

    });

    return {
        ...query,
        data: extractQueryDataArray<Type>(query),
    }
}

export const useQueryArrayForm = <Type extends object>(
    key: QueryKey,
    apiToCall: () => Promise<Array<Type>>,
    typeName: string,
    staleTime: StaleTimeEnum = StaleTimeEnum.DEFAULT
): UseQueryArrayResult<Type> => {

    const [isRefetched, setIsRefetched] = useState(false);

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const query = useQuery<Array<Type>>(key, apiToCall, {

        onError: (error: any) => {


            validator.validate(error.response)
            alert.error(`Error while fetching ${typeName}`, error.response?.data?.Message)
        },

        select: (data: Array<Type>) => data.map((rec: Type) => convertObject<Type>(rec)),

        staleTime: staleTime,

        enabled: false,

        refetchOnWindowFocus: false

    });

    useEffect(() => {
        if (!isRefetched) {
            query.refetch().then(() => setIsRefetched(true))
        }
    }, [query, isRefetched])

    return {
        ...query,
        data: extractQueryDataArray<Type>(query),
    }
}

//##############################################
//## QUERY DATA OBJECT
//##############################################

export const extractQueryDataObject = <Type>(query: UseQueryResult<Type | null>, defaultObject: Type): Type => {
    const data = isNull(query.data) || isEmpty(query.data) || isUndefined(query.data) ? defaultObject : query.data
    return data
}

export const extractDataObject = <Type>(query: UseQueryResult<Type | null>): Type => {
    const data = isNull(query.data) || isEmpty(query.data) || isUndefined(query.data) ? {} as Type : query.data
    return data
}

export interface UseQueryObjectResult<Type> {
    data: Type,
    isLoading: boolean,
    refetch: () => void,
    isFetching: boolean,
    lastUpdatedAt: Date
}

export interface useQueryObjectFormResult<Type> {
    data: Type,
    isLoading: boolean,
    refetch: () => void,
    key: QueryKey,
    isFetching: boolean
}

export const useQueryObjectForm = <Type extends object>(
    key: QueryKey,
    apiToCall: () => Promise<Type>,
    typeName: string
): useQueryObjectFormResult<Type> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const queryKey = key.concat("FORM")

    const { refetch, data, isLoading, isFetching } = useQuery<Type>(queryKey, apiToCall, {

        onError: (error: any) => {
            validator.validate(error.response)
            alert.error(`Error while fetching ${typeName}`, error.response?.data?.Message)
        },

        select: (data: Type) => convertObject<Type>(data),

        staleTime: Infinity,

        refetchOnWindowFocus: false,
    });

    return {
        data: extractDataObjectData<Type>(data),
        isLoading: isLoading,
        refetch: refetch,
        key: queryKey,
        isFetching: isFetching
    }
}

export const extractDataObjectData = <Type>(queryData: Type | undefined): Type => {
    const data = isNull(queryData) || isEmpty(queryData) || isUndefined(queryData) ? {} as Type : queryData
    return data
}

export const useQueryObject = <Type extends object>(
    key: QueryKey,
    apiToCall: () => Promise<Type>,
    typeName: string,
    staleTime: StaleTimeEnum = StaleTimeEnum.DEFAULT,
    refetchOnWindowFocus?: boolean
): UseQueryObjectResult<Type> => {

    const alert = useAlert();

    const validator = useApiResponseErrorValidator();

    const [isRefetched, setIsRefetched] = useState(false)

    const query = useQuery<Type>(key, apiToCall, {

        onError: (error: any) => {
            validator.validate(error.response)
            alert.error(`Error while fetching ${typeName}`, error.response?.data?.Message)
        },

        select: (data: Type) => convertObject<Type>(data),

        staleTime: staleTime,
        refetchInterval: staleTime,
        refetchOnWindowFocus: refetchOnWindowFocus
    });

    useEffect(() => {
        if (!isRefetched) {
            query.refetch().then(() => setIsRefetched(true))
        }
    }, [query, isRefetched])


    return {
        data: extractDataObject<Type>(query),
        isLoading: query.isLoading,
        refetch: query.refetch,
        isFetching: query.isFetching,
        lastUpdatedAt: new Date(query.dataUpdatedAt)
    }
}
